import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { allArticles, articleContentMap } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();

  const article = allArticles.find((a) => a.slug === slug);
  const contentData = articleContentMap[slug || ''] || {
    author: '吃瓜小编',
    content: '<p>文章内容加载中...</p>',
  };

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-20 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-950">文章未找到</h1>
            <Link to="/" className="mt-4 inline-block text-primary-600 hover:text-primary-700 cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = allArticles
    .filter((a) => a.slug !== slug && (a.categorySlug === article.categorySlug || a.category === article.category))
    .slice(0, 3);

  const pageUrl = `https://chiguawang.com/article/${article.slug}`;

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: t('breadcrumbHome'),
        item: 'https://chiguawang.com',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: article.category,
        item: `https://chiguawang.com/category/${article.categorySlug}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article.title,
        item: pageUrl,
      },
    ],
  };

  return (
    <>
      <Navbar />
      <main>
        <article className="bg-background-50 pt-20 md:pt-24 pb-8 md:pb-12">
          <div className="px-4 md:px-6 max-w-3xl mx-auto">
            <Breadcrumb
              items={[
                { label: t('breadcrumbHome'), to: '/' },
                { label: article.category, to: `/category/${article.categorySlug}` },
                { label: article.title },
              ]}
            />

            <div className="text-center mt-8">
              <Link
                to={`/category/${article.categorySlug}`}
                className="inline-block px-4 py-1.5 text-xs font-medium text-primary-700 border border-primary-300 rounded-full hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                {article.category}
              </Link>
              <h1 className="mt-6 text-2xl md:text-4xl font-heading font-bold text-foreground-950 leading-tight max-w-2xl mx-auto">
                {article.title}
              </h1>
              <div className="mt-6 flex items-center justify-center gap-4 text-sm text-foreground-400">
                <span>{contentData.author}</span>
                <span>·</span>
                <span>{article.date}</span>
                <span>·</span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line" />
                  {article.viewCount.toLocaleString()} {t('views')}
                </span>
              </div>
            </div>

            <div className="mt-8">
              <div className="rounded-xl overflow-hidden">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  className="w-full aspect-[16/9] object-cover object-top"
                />
              </div>
            </div>

            <div
              className="mt-10 prose prose-lg max-w-none text-foreground-800 leading-relaxed
                [&_h2]:text-xl [&_h2]:md:text-2xl [&_h2]:font-heading [&_h2]:font-bold [&_h2]:text-foreground-950 [&_h2]:mt-10 [&_h2]:mb-4
                [&_p]:text-base [&_p]:leading-relaxed [&_p]:mb-4 [&_p]:text-foreground-700
                [&_strong]:text-foreground-950"
              dangerouslySetInnerHTML={{ __html: contentData.content }}
            />

            <div className="mt-10 flex flex-wrap gap-2">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 text-xs bg-background-100 text-foreground-500 rounded-full border border-background-200/70"
                >
                  #{tag}
                </span>
              ))}
            </div>

            <div className="mt-12 p-6 md:p-8 bg-background-100 rounded-xl border border-background-200/70">
              <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">网友热评</h2>
              <blockquote className="border-l-4 border-accent-400 pl-5">
                <p className="text-lg font-heading font-semibold text-foreground-900 leading-relaxed">
                  &ldquo;这才是真正的吃瓜第一站！每次都是这里最先看到爆料，比其他平台快太多了&rdquo;
                </p>
                <footer className="mt-4 flex items-center gap-3">
                  <span className="text-sm font-semibold text-foreground-800">热心网友小王</span>
                  <span className="flex items-center gap-1 text-xs text-accent-600">
                    <i className="ri-verified-badge-fill" />
                    已认证用户
                  </span>
                </footer>
              </blockquote>
              <div className="mt-6 flex flex-wrap gap-4 text-xs text-foreground-400">
                <span className="flex items-center gap-1"><i className="ri-thumb-up-line" /> 12.3k 赞</span>
                <span className="flex items-center gap-1"><i className="ri-chat-3-line" /> 2.1k 回复</span>
                <span className="flex items-center gap-1"><i className="ri-share-forward-line" /> 分享</span>
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="bg-background-100 py-12 md:py-16">
            <div className="px-4 md:px-6 max-w-7xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedArticles.map((ra) => (
                  <ArticleCard
                    key={ra.id}
                    slug={ra.slug}
                    title={ra.title}
                    excerpt={ra.excerpt}
                    category={ra.category}
                    categorySlug={ra.categorySlug}
                    imageUrl={ra.imageUrl}
                    date={ra.date}
                    viewCount={ra.viewCount}
                    commentCount={ra.commentCount}
                  />
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="bg-background-50 py-12 md:py-16">
          <div className="px-4 md:px-6 max-w-xl mx-auto text-center">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
              还没吃够？
            </h2>
            <p className="mt-2 text-sm text-foreground-500">
              更多精彩爆料，尽在吃瓜网
            </p>
            <Link
              to="/"
              className="whitespace-nowrap mt-6 inline-flex items-center gap-2 px-8 py-3 bg-primary-600 text-background-50 text-sm font-medium rounded-full hover:bg-primary-700 transition-all cursor-pointer"
            >
              探索更多热瓜
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </section>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Article',
              headline: article.title,
              description: article.excerpt,
              image: article.imageUrl,
              datePublished: article.date,
              author: {
                '@type': 'Person',
                name: contentData.author,
              },
              publisher: {
                '@type': 'Organization',
                name: '吃瓜网',
                url: 'https://chiguawang.com',
              },
              mainEntityOfPage: {
                '@type': 'WebPage',
                '@id': pageUrl,
              },
            }),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
        />
      </main>
      <Footer />
    </>
  );
}